import UnexpectedResponseError from "../classes/unexpectedResponseError";
import describeResponse from "./describeResponse";
import reportUnexpectedResponse from "./reportUnexpectedResponse";

// Rack::RequestId marks the responses the app returns. One without the mark came from something on
// the way: the client's proxy or firewall, our load balancer or nginx, or, mid-deploy, a server not
// yet running the marker.
const markerHeader = "x-cyg-request-id";

// Browsers fail the requests still in flight when the user leaves the page, the same way as a
// dropped connection. The flag clears itself, since a file download fires `beforeunload` too and
// leaves the page in place.
const leavingWindowMs = 3000;
let leavingPage = false;
let leavingTimer = null;

window.addEventListener("beforeunload", () => {
  leavingPage = true;
  clearTimeout(leavingTimer);
  leavingTimer = setTimeout(() => {
    leavingPage = false;
  }, leavingWindowMs);
});

const isJson = (header) => /json/i.test(header("content-type") || "");

const classify = ({ status, requestId, header, expectsJson }) => {
  if (status === 0) {
    return "network";
  }

  if (!requestId) {
    return /^awselb/i.test(header("server") || "") ? "unavailable" : "intermediary";
  }

  if (status >= 500) {
    return "server_error";
  }

  if (expectsJson && !isJson(header)) {
    return "unexpected_format";
  }

  return null;
};

// Block pages are error pages that are not JSON. A Cygnetise response can also arrive without the
// marker, from a server not yet running it during a deploy, and its data is in 2xx pages and JSON,
// so reading only these keeps that data out of reports.
const isReadableErrorPage = ({ status, header }) => status >= 400 && !isJson(header);

// Returns the kind of problem with a response, or null when it is usable, and reports a problem
// without waiting for the report. `readBody` is called at once, so a fetch body can be cloned
// before the caller consumes it.
export default function checkResponse({ method, url, status, header, readBody, expectsJson, silent }) {
  const requestId = header(markerHeader);
  const kind = classify({ status, requestId, header, expectsJson });

  if (!kind) {
    return null;
  }

  if (kind === "network" && leavingPage) {
    return kind;
  }

  const readReportedBody = kind === "intermediary" && isReadableErrorPage({ status, header }) ? readBody : () => "";

  // Reporting never gets in the caller's way, so a body that cannot be read is reported as empty.
  new Promise((resolve) => resolve(readReportedBody()))
    .catch(() => "")
    .then((text) => {
      const details = describeResponse({ method, url, status, requestId, header, body: text });
      reportUnexpectedResponse(new UnexpectedResponseError(kind, details), { silent });
    });

  return kind;
}
